import React from 'react';
import { Box, Paper, Typography, Stack, Divider, IconButton, alpha, Chip } from '@mui/material';
import {
  Phone,
  Email,
  Business,
  PeopleAltOutlined,
  Add,
  Person,
  Article,
  LocalOfferOutlined,
} from '@mui/icons-material';
import { TooltipUI } from '../../../Interface';
import { sxConfig } from '../workspace.style';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import WorkspaceTagMenu from './WorkspaceTagMenu';

const EmailField = ({ email }) => {
  const handleCopy = async () => {
    await navigator.clipboard.writeText(email);

    addNotification({
      content: 'E-mail cím a vágólapra másolva!',
      type: NotificationType.SUCCESS,
    });
  };

  return (
    <Stack direction="row" spacing={1} mt={1} sx={{ minWidth: 0 }}>
      <Email fontSize="small" sx={{ flexShrink: 0 }} />
      <TooltipUI title={email} arrow>
        <Typography
          color="primary.main"
          onClick={handleCopy}
          noWrap
          sx={{
            minWidth: 0,
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {email}
        </Typography>
      </TooltipUI>
    </Stack>
  );
};

const WorkspaceSidebar = ({ selectedValues, projectTagLabels, handleUpdateProjectTag }) => {
  return (
    <Paper
      sx={{
        ...sxConfig.card,
        p: 2,
      }}
    >
      {selectedValues.company && (
        <Box mb={3}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            <IconButton
              sx={{
                m: 0,
                p: 0.5,
                background: '#4ade80',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#38c96d',
                },
              }}
            >
              <Business sx={{ color: 'white' }} />
            </IconButton>
            <Typography
              fontWeight={700}
              sx={{
                overflowWrap: 'anywhere',
                minWidth: 0,
              }}
            >
              {selectedValues.company.name}
            </Typography>
          </Stack>
          <Box>
            {selectedValues.company?.phoneNumber && (
              <Stack direction="row" spacing={1} mt={1}>
                <Phone fontSize="small" />
                <Typography color="primary.main">{selectedValues.company.phoneNumber}</Typography>
              </Stack>
            )}
            {selectedValues.company?.email && <EmailField email={selectedValues.company.email} />}
          </Box>
        </Box>
      )}
      <Box mb={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton
              sx={{
                m: 0,
                p: 0.5,
                background: '#8B5CF6',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#7C3AED',
                },
              }}
            >
              <LocalOfferOutlined sx={{ color: 'white' }} />
            </IconButton>
            <Typography fontWeight={700}>Címkék</Typography>
          </Stack>
          <WorkspaceTagMenu
            tags={projectTagLabels}
            selectedTags={selectedValues.tagProjectLink}
            onTagChange={handleUpdateProjectTag}
          />
        </Stack>
        {selectedValues.tagProjectLink?.length > 0 && (
          <Box>
            <Stack direction="row" gap={0.5} mt={1} flexWrap="wrap">
              {selectedValues.tagProjectLink.map((tagLink) => {
                const tag = tagLink.tag;

                return (
                  <Chip
                    key={tag.id}
                    label={tag.name}
                    size="small"
                    sx={{
                      fontSize: 11,
                      bgcolor: alpha(tag.color, 0.2),
                      color: tag.color,
                      borderRadius: '5px',
                      fontWeight: 'bold',
                      minWidth: '20px',

                      '&:hover': {
                        bgcolor: alpha(tag.color, 0.3),
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        )}
      </Box>
      <Box mb={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton
              sx={{
                m: 0,
                p: 0.5,
                background: '#ffbb29',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#ecad24',
                },
              }}
            >
              <PeopleAltOutlined sx={{ color: 'white' }} />
            </IconButton>
            <Typography fontWeight={700}>Kapcsolatok</Typography>
          </Stack>
          <IconButton>
            <Add />
          </IconButton>
        </Stack>
        {selectedValues.company?.companyContact?.length > 0 && (
          <Stack mt={2} divider={<Divider sx={{ my: 2 }} />}>
            {selectedValues.company.companyContact?.map((c) => (
              <Box key={c.id}>
                <Stack direction="row" spacing={1}>
                  <Person fontSize="small" sx={{ flexShrink: 0 }} />
                  <Typography
                    fontWeight={700}
                    sx={{
                      overflowWrap: 'anywhere',
                      minWidth: 0,
                    }}
                  >
                    {c.name}
                  </Typography>
                </Stack>
                <Typography pl={3.5} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  {c.role}
                </Typography>
                {c.phoneNumber && (
                  <Stack direction="row" spacing={1} mt={1}>
                    <Phone fontSize="small" />
                    <Typography color="primary.main">{c.phoneNumber}</Typography>
                  </Stack>
                )}
                {c.email && <EmailField email={c.email} />}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton
              sx={{
                m: 0,
                p: 0.5,
                background: '#6694f7',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#4D7FE8',
                },
              }}
            >
              <Article sx={{ color: 'white' }} />
            </IconButton>
            <Typography fontWeight={700}>Adatlapok</Typography>
          </Stack>
          <IconButton>
            <Add />
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
};

export default WorkspaceSidebar;
